import type { MainNavRoute, MainPath } from '../types';

import PERMISSIONS from '@snowit/utilities/permissions';

import { makeChildren } from '../utils';

export const NotificationsPath: MainPath = '/notifications';
export const StageRoutesPath: MainPath = '/routes/:id?';
export const DeparturesPath: MainPath = '/departures/:id?';
export const CarouselsPath: MainPath = '/carousels/:id?/:section?';
export const PartnersPath: MainPath = '/partners/:id?/:section?';
export const ReviewsPath: MainPath = '/reviews/:id?/:section?';
export const DistrictsPath: MainPath = '/districts/:id?/:section?';
export const PoliciesPath: MainPath = '/policies/:id?';
export const SnowitcardsPath: MainPath = '/snowitcards/:id?';
export const GalleriesPath: MainPath = '/galleries/:id?';
export const ShippingPath: MainPath = '/shipping_methods/:id?';
export const FileDrivePath: MainPath = '/file_drive/*?';

export const DISTRICTS_TABS = ['info', 'zones'] as const;

export type DistrictsTabs = (typeof DISTRICTS_TABS)[number];

export const CAROUSELS_TABS = ['general', 'items'] as const;
export type CarouselsTabs = (typeof CAROUSELS_TABS)[number];

export const PARTNERS_TABS = [
  'info',
  'config',
  'email',
  'texts',
  'opening',
  'stripe',
  'admin_notes',
] as const;
export type PartnersTabs = (typeof PARTNERS_TABS)[number];

export const NotificationsRoute: MainNavRoute = {
  path: '/notifications',
  label: 'routes.notifications',
  icon: 'bell',
  type: 0,
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-55175/3515f7621828f02',
};

export const DeparturesRoute: MainNavRoute = {
  path: '/departures',
  label: 'routes.departures',
  icon: 'route',
  type: 1,
  //todo: @matey, change permissions
  permissions: [PERMISSIONS.CAROUSELS__LIST],
  children: [
    {
      path: ':id',
      permissions: [PERMISSIONS.CAROUSELS__DETAILS],
    },
  ],
};

export const StageRoutesRoute: MainNavRoute = {
  path: '/routes',
  label: 'routes.routes',
  type: 1,
  icon: 'road',
  permissions: [PERMISSIONS.ROUTES__LIST],
  children: [
    {
      path: ':id',
      permissions: [PERMISSIONS.ROUTES__DETAILS],
    },
  ],
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-55195/4cd4d020e5b4a1b',
};

export const CarouselsRoute: MainNavRoute = {
  path: '/carousels',
  label: 'routes.carousels',
  type: 1,
  permissions: [PERMISSIONS.CAROUSELS__LIST],
  icon: 'carousel',
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-55215/89bad397c899b2a',
  children: [
    {
      path: ':id',
      permissions: [PERMISSIONS.CAROUSELS__DETAILS],
      children: makeChildren<CarouselsTabs>({
        general: { path: 'general' },
        items: { path: 'items' },
      }),
    },
  ],
};

export const PartnersRoute: MainNavRoute = {
  path: '/partners',
  label: 'routes.partners',
  type: 1,
  icon: 'partner',
  permissions: [PERMISSIONS.PARTNERS__LIST],
  children: [
    {
      path: ':id',
      permissions: [PERMISSIONS.PARTNERS__DETAILS],
      children: makeChildren<PartnersTabs>({
        info: { path: 'info' },
        email: { path: 'email' },
        config: { path: 'config' },
        texts: { path: 'texts' },
        stripe: { path: 'stripe' },
        opening: { path: 'opening' },
        admin_notes: { path: 'admin_notes' },
      }),
    },
  ],
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-24021/35ddd1f6d84ec88',
};

export const ReviewsRoute: MainNavRoute = {
  path: '/reviews',
  label: 'routes.reviews',
  type: 1,
  icon: 'star',
  permissions: [PERMISSIONS.REVIEWS__LIST],
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-55235/b26f6a02d3bd089',
  children: [
    {
      path: ':id',
      permissions: [PERMISSIONS.REVIEWS__DETAILS],
    },
  ],
};

export const DistrictsRoute: MainNavRoute = {
  path: '/districts',
  label: 'routes.districts',
  icon: 'district',
  type: 1,
  permissions: [PERMISSIONS.DISTRICTS__LIST],
  children: [
    {
      path: ':id',
      permissions: [PERMISSIONS.DISTRICTS__DETAILS],
      children: makeChildren<DistrictsTabs>({
        info: { path: 'info' },
        zones: { path: 'zones' },
      }),
    },
  ],
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-24101/332a6778919e0af',
};

export const SnowitcardsRoute: MainNavRoute = {
  path: '/snowitcards',
  label: 'routes.snowitcards',
  icon: 'snowitcard',
  type: 1,
  permissions: [PERMISSIONS.SNOWITCARDS__LIST],
  children: [
    {
      hidden: true,
      path: ':id',
      permissions: [PERMISSIONS.SNOWITCARDS__DETAILS],
    },
  ],
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-24221/f93e48392e8908e',
};

export const GalleriesRoute: MainNavRoute = {
  path: '/galleries',
  label: 'routes.galleries',
  icon: 'gallery',
  type: 1,
  permissions: [PERMISSIONS.GALLERIES__LIST],
  children: [
    {
      hidden: true,
      path: ':id',
      permissions: [PERMISSIONS.GALLERIES__DETAILS],
    },
  ],
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-24201/fd67578a9552c91',
};

export const PoliciesRoute: MainNavRoute = {
  path: '/policies',
  label: 'routes.policies',
  icon: 'book_open_cover',
  type: 1,
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-24241/0b363d80ad9b43b',
  permissions: [PERMISSIONS.POLICIES__LIST],
  children: [
    {
      path: ':id',
      permissions: [PERMISSIONS.POLICIES__DETAILS],
    },
  ],
};

export const ShippingRoute: MainNavRoute = {
  path: '/shipping_methods',
  label: 'routes.shipping_methods',
  icon: 'truck',
  children: [
    {
      path: ':id',
      permissions: [PERMISSIONS.SHIPPING_METHODS__DETAILS],
    },
  ],
};

export const PdfManagerRoute: MainNavRoute = {
  path: '/file_drive',
  label: 'routes.file_drive',
  icon: 'file_pdf',
  children: [
    {
      path: '(.+)?',
    },
  ],
};
